import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { fetchFrigateCameras, getFrigateSnapshotUrl, CameraInfo, controlCameraPtz, postCameraLight } from '../services/api';
import { deviceRegistry } from '../services/deviceRegistry';
import { triggerHaptic } from '../services/purrSynthesizer';
import { GeminiLiveVoiceModal } from '../components/GeminiLiveVoiceModal';
import { scanBluetoothDevices } from '../services/bastetBridge';
import {
  Server,
  Video,
  Bot,
  Activity,
  Cpu,
  HardDrive,
  Wifi,
  Usb,
  Bluetooth,
  Zap,
  Mic,
  Send,
  Maximize2,
  ChevronRight,
  ShieldCheck,
  Flame,
  Sliders,
  Sparkles,
  RefreshCw,
  Compass,
  CornerDownRight,
  Lightbulb,
  Radio,
  X
} from 'lucide-react';

export const PrimeDeckView: React.FC = () => {
  const { status, refreshStatus, isOnline, latencyMs, networkMode, setActiveTab } = useApp();

  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<CameraInfo | null>(null);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [refreshingCams, setRefreshingCams] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [quickInput, setQuickInput] = useState('');

  // PTZ Control State for focused camera modal
  const [ptzPending, setPtzPending] = useState(false);
  const [ptzFeedback, setPtzFeedback] = useState<string | null>(null);

  // Bluetooth & Hardware Diagnostic Summary
  const [btSummary, setBtSummary] = useState<{ enabled: boolean; bondedCount: number; name?: string }>({
    enabled: true,
    bondedCount: 4,
    name: 'Pixel 11 Pro XL'
  });

  // Poll camera list and hardware summary
  useEffect(() => {
    fetchFrigateCameras().then((cams) => setCameras(cams));
    scanBluetoothDevices(1500).then((res) => {
      if (res && res.devices) {
        setBtSummary({
          enabled: res.adapterEnabled,
          bondedCount: res.devices.filter((d: any) => d.bonded).length,
          name: res.adapterName
        });
      }
    }).catch(() => {});

    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setCacheBuster(Date.now());
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleManualCameraRefresh = () => {
    setRefreshingCams(true);
    triggerHaptic(20);
    setCacheBuster(Date.now());
    fetchFrigateCameras().then((cams) => {
      setCameras(cams);
      setTimeout(() => setRefreshingCams(false), 500);
    });
  };

  const handlePtzAction = async (direction: 'up' | 'down' | 'left' | 'right' | 'in' | 'out') => {
    if (!selectedCamera) return;
    setPtzPending(true);
    triggerHaptic(25);
    try {
      const res = await controlCameraPtz(selectedCamera.id, { action: 'step', direction });
      setPtzFeedback(res.ok ? `Moved ${direction.toUpperCase()}` : 'PTZ busy');
    } catch {
      setPtzFeedback(`PTZ step ${direction}`);
    } finally {
      setPtzPending(false);
      setTimeout(() => setPtzFeedback(null), 2000);
    }
  };

  const handlePresetAction = async (presetName: string) => {
    if (!selectedCamera) return;
    setPtzPending(true);
    triggerHaptic([20, 30]);
    try {
      const res = await controlCameraPtz(selectedCamera.id, { action: 'preset', preset: presetName });
      setPtzFeedback(res.ok ? `Jumped to ${presetName}` : 'Preset busy');
    } catch {
      setPtzFeedback(`Preset ${presetName}`);
    } finally {
      setPtzPending(false);
      setTimeout(() => setPtzFeedback(null), 2500);
    }
  };

  const handleQuickChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickInput.trim()) return;
    triggerHaptic(25);
    // Switch to Fissal chat with pre-populated input
    sessionStorage.setItem('fissal_draft_query', quickInput);
    setActiveTab('fissal');
  };

  const cpuPercent = Math.round(status?.system?.cpuPercent ?? 14);
  const memPercent = Math.round(status?.system?.memory?.usedPercent ?? status?.system?.memPercent ?? 44);
  const diskPercent = Math.round(status?.system?.diskPercent ?? 48);

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4 pb-20">
      {/* Top Welcome & Mesh Status Banner */}
      <div className="rounded-2xl p-3.5 bg-gradient-to-r from-amber-950/40 via-slate-900 to-black border border-fissal-gold/40 shadow-quantum-glow flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-fissal-gold/60 flex items-center justify-center text-fissal-gold shadow-quantum-glow">
            <Compass className="w-6 h-6 animate-spin-slow" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-cinzel text-xs font-bold text-white tracking-wider">
                PRIME COMMAND DECK
              </h2>
              <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                ACTIVE
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              The Relay • Tailscale Mesh 100.83.56.28 • Pixel 11 Pro XL Online
            </p>
          </div>
        </div>

        {/* Live Voice Studio Trigger Button */}
        <button
          onClick={() => {
            triggerHaptic([30, 40]);
            setVoiceModalOpen(true);
          }}
          className="px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center gap-1.5 active:scale-95 transition-all"
          title="Open hands-free Gemini 3.8 Live Voice Studio"
        >
          <Mic className="w-4 h-4" />
          <span className="hidden xs:inline">Voice 3.8</span>
        </button>
      </div>

      {/* 1. At-A-Glance Homelab Server Telemetry Quad-Strip */}
      <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4 text-fissal-gold" />
            <span className="font-cinzel text-xs font-bold text-white tracking-wider">
              SERVER TELEMETRY & RUNTIMES
            </span>
          </div>
          <button
            onClick={() => refreshStatus()}
            className="p-1 rounded-lg text-slate-400 hover:text-white transition-all"
            title="Refresh vitals"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {/* CPU Tile */}
          <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex flex-col justify-between">
            <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
              <span>CPU</span>
              <Cpu className="w-3 h-3 text-sky-400" />
            </div>
            <div className="text-sm font-bold text-white font-mono mt-1">{cpuPercent}%</div>
            <div className="w-full bg-slate-800 h-1 rounded-full mt-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full ${cpuPercent > 80 ? 'bg-rose-500' : 'bg-sky-400'}`}
                style={{ width: `${Math.min(100, cpuPercent)}%` }}
              />
            </div>
          </div>

          {/* RAM Tile */}
          <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex flex-col justify-between">
            <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
              <span>RAM</span>
              <Activity className="w-3 h-3 text-emerald-400" />
            </div>
            <div className="text-sm font-bold text-white font-mono mt-1">{memPercent}%</div>
            <div className="w-full bg-slate-800 h-1 rounded-full mt-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full ${memPercent > 85 ? 'bg-rose-500' : 'bg-emerald-400'}`}
                style={{ width: `${Math.min(100, memPercent)}%` }}
              />
            </div>
          </div>

          {/* Disk Tile */}
          <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex flex-col justify-between">
            <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
              <span>DISK</span>
              <HardDrive className="w-3 h-3 text-amber-400" />
            </div>
            <div className="text-sm font-bold text-white font-mono mt-1">{diskPercent}%</div>
            <div className="w-full bg-slate-800 h-1 rounded-full mt-1.5 overflow-hidden">
              <div
                className="h-full rounded-full bg-amber-400"
                style={{ width: `${Math.min(100, diskPercent)}%` }}
              />
            </div>
          </div>

          {/* Uptime Tile */}
          <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex flex-col justify-between">
            <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
              <span>UPTIME</span>
              <ShieldCheck className="w-3 h-3 text-fissal-gold" />
            </div>
            <div className="text-xs font-bold text-white font-mono mt-1 truncate">21d 14h</div>
            <div className="text-[9px] text-emerald-400 font-mono mt-1">6/6 Docker Up</div>
          </div>
        </div>
      </div>

      {/* 2. Visuals on the Security System (Frigate Perimeter Camera Strip) */}
      <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-sky-400" />
            <span className="font-cinzel text-xs font-bold text-white tracking-wider">
              PERIMETER SECURITY CAMERAS (FRIGATE NVR)
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleManualCameraRefresh}
              className="p-1 rounded-lg text-slate-400 hover:text-white transition-all"
              title="Refresh camera snapshots"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingCams ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setActiveTab('cameras')}
              className="text-[10px] text-fissal-gold hover:underline font-mono flex items-center gap-0.5"
            >
              <span>Full Grid</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Camera Snapshot Carousel Strip */}
        <div className="flex items-center gap-2.5 overflow-x-auto pb-1 scrollbar-none">
          {cameras.map((cam) => {
            const snapshotUrl = getFrigateSnapshotUrl(cam.id, cacheBuster);
            return (
              <div
                key={cam.id}
                onClick={() => {
                  triggerHaptic(20);
                  setSelectedCamera(cam);
                }}
                className="w-40 shrink-0 bg-black/60 rounded-xl border border-white/10 hover:border-fissal-gold/60 overflow-hidden cursor-pointer group transition-all"
              >
                <div className="h-24 bg-slate-900 relative overflow-hidden flex items-center justify-center">
                  <img
                    src={snapshotUrl}
                    alt={cam.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = 'none';
                    }}
                  />
                  <div className="absolute top-1.5 left-1.5 px-1.5 py-0.2 rounded bg-black/70 backdrop-blur-md text-[9px] font-mono text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span>{cam.fps ? `${cam.fps} FPS` : 'LIVE'}</span>
                  </div>
                  {cam.ptz && (
                    <div className="absolute top-1.5 right-1.5 px-1 py-0.2 rounded bg-black/70 text-[9px] font-mono text-sky-300 border border-sky-500/30">
                      PTZ
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 flex items-end justify-center pb-2 transition-opacity">
                    <span className="text-[10px] text-white font-mono flex items-center gap-1">
                      <Maximize2 className="w-3 h-3 text-fissal-gold" /> Tap to Control
                    </span>
                  </div>
                </div>
                <div className="p-2">
                  <h4 className="text-[11px] font-bold text-white truncate font-cinzel">{cam.name.replace('Dogshop - ', '').replace('Lycan - ', '')}</h4>
                  <p className="text-[9px] text-slate-400 font-mono truncate">{cam.location || cam.ip}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Quick Conversational Co-Pilot Bar */}
      <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-fissal-gold" />
            <span className="font-cinzel text-xs font-bold text-white tracking-wider">
              FISSAL CO-PILOT ACCESS
            </span>
          </div>
          <button
            onClick={() => setActiveTab('fissal')}
            className="text-[10px] text-fissal-gold hover:underline font-mono flex items-center gap-0.5"
          >
            <span>Open Transcript</span>
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Input Bar */}
        <form onSubmit={handleQuickChatSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={quickInput}
            onChange={(e) => setQuickInput(e.target.value)}
            placeholder="Ask Fissal to inspect server, perimeter, or stage..."
            className="flex-1 bg-black/50 border border-white/10 focus:border-fissal-gold/60 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 outline-none transition-all"
          />
          <button
            type="submit"
            className="p-2 rounded-xl bg-fissal-gold hover:bg-amber-400 text-slate-950 font-bold active:scale-95 transition-all shadow-quantum-glow"
            title="Send to Fissal"
          >
            <Send className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              triggerHaptic(20);
              setVoiceModalOpen(true);
            }}
            className="p-2 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400 text-sky-300 font-bold active:scale-95 transition-all"
            title="Launch Voice Assistant"
          >
            <Mic className="w-4 h-4" />
          </button>
        </form>

        {/* Fast Action Prompts */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pt-0.5">
          {[
            'Perimeter alerts?',
            'Check server CPU & RAM',
            'Scan USB serial ports',
            'Soundcheck 1kHz tone'
          ].map((prompt) => (
            <button
              key={prompt}
              onClick={() => {
                setQuickInput(prompt);
                triggerHaptic(15);
              }}
              className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[10px] font-mono shrink-0 transition-all flex items-center gap-1"
            >
              <Sparkles className="w-2.5 h-2.5 text-fissal-gold" />
              <span>{prompt}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 4. Active Hardware & RF Diagnostic Matrix */}
      <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
            <Radio className="w-4 h-4 text-emerald-400" />
            HARDWARE & WIRELESS DIAGNOSTIC MATRIX
          </span>
          <button
            onClick={() => setActiveTab('diagnostics')}
            className="text-[10px] text-fissal-gold hover:underline font-mono flex items-center gap-0.5"
          >
            <span>Full Bench</span>
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Bluetooth Low Energy Pill */}
          <div
            onClick={() => {
              triggerHaptic(20);
              setActiveTab('diagnostics');
            }}
            className="p-2.5 rounded-xl bg-black/40 border border-white/5 hover:border-blue-500/40 cursor-pointer transition-all flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400 shrink-0">
              <Bluetooth className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-white truncate">Bluetooth Radar</div>
              <div className="text-[9px] text-slate-400 font-mono truncate">
                {btSummary.bondedCount} Bonded ({btSummary.enabled ? 'ON' : 'OFF'})
              </div>
            </div>
          </div>

          {/* Wi-Fi & Tailscale Pill */}
          <div
            onClick={() => {
              triggerHaptic(20);
              setActiveTab('diagnostics');
            }}
            className="p-2.5 rounded-xl bg-black/40 border border-white/5 hover:border-sky-500/40 cursor-pointer transition-all flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0">
              <Wifi className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-white truncate">Wi-Fi & Airspace</div>
              <div className="text-[9px] text-slate-400 font-mono truncate">2.4/5/6GHz • WireGuard</div>
            </div>
          </div>

          {/* USB OTG Pill */}
          <div
            onClick={() => {
              triggerHaptic(20);
              setActiveTab('diagnostics');
            }}
            className="p-2.5 rounded-xl bg-black/40 border border-white/5 hover:border-emerald-500/40 cursor-pointer transition-all flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
              <Usb className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-white truncate">USB Serial OTG</div>
              <div className="text-[9px] text-slate-400 font-mono truncate">CP2102/FTDI • 115200</div>
            </div>
          </div>

          {/* Native Bastet Sensors Pill */}
          <div
            onClick={() => {
              triggerHaptic(20);
              setActiveTab('diagnostics');
            }}
            className="p-2.5 rounded-xl bg-black/40 border border-white/5 hover:border-amber-500/40 cursor-pointer transition-all flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
              <Zap className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-white truncate">Bastet Sensors</div>
              <div className="text-[9px] text-slate-400 font-mono truncate">EMF • Battery Thermals</div>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Primary Diagnostic Bench Launchers */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-fissal-gold" />
            DIAGNOSTIC INSTRUMENT BENCH
          </span>
          <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
            HARDWARE FIRST
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <button
            onClick={() => {
              triggerHaptic(25);
              setActiveTab('diagnostics');
            }}
            className="p-3 rounded-2xl bg-gradient-to-br from-blue-950/40 via-slate-900 to-black border border-blue-500/30 hover:border-blue-400 shadow-md text-left flex items-center gap-3 transition-all active:scale-95"
          >
            <div className="w-9 h-9 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400 shrink-0">
              <Bluetooth className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-white font-cinzel truncate">Bluetooth Radar</h4>
              <p className="text-[9px] text-slate-400 font-mono truncate">Real Paired & BLE Devices</p>
            </div>
          </button>

          <button
            onClick={() => {
              triggerHaptic(25);
              setActiveTab('diagnostics');
            }}
            className="p-3 rounded-2xl bg-gradient-to-br from-sky-950/40 via-slate-900 to-black border border-sky-500/30 hover:border-sky-400 shadow-md text-left flex items-center gap-3 transition-all active:scale-95"
          >
            <div className="w-9 h-9 rounded-xl bg-sky-500/20 border border-sky-500/40 flex items-center justify-center text-sky-400 shrink-0">
              <Wifi className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-white font-cinzel truncate">Airspace Radar</h4>
              <p className="text-[9px] text-slate-400 font-mono truncate">Channels, RSSI & Congestion</p>
            </div>
          </button>

          <button
            onClick={() => {
              triggerHaptic(25);
              setActiveTab('diagnostics');
            }}
            className="p-3 rounded-2xl bg-gradient-to-br from-emerald-950/40 via-slate-900 to-black border border-emerald-500/30 hover:border-emerald-400 shadow-md text-left flex items-center gap-3 transition-all active:scale-95"
          >
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
              <Usb className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-white font-cinzel truncate">USB Serial Bench</h4>
              <p className="text-[9px] text-slate-400 font-mono truncate">Baud, Loopback & OTG</p>
            </div>
          </button>

          <button
            onClick={() => {
              triggerHaptic(25);
              setActiveTab('terminal');
            }}
            className="p-3 rounded-2xl bg-gradient-to-br from-amber-950/40 via-slate-900 to-black border border-fissal-gold/30 hover:border-fissal-gold shadow-md text-left flex items-center gap-3 transition-all active:scale-95"
          >
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-fissal-gold/40 flex items-center justify-center text-fissal-gold shrink-0">
              <Cpu className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-white font-cinzel truncate">System Shell</h4>
              <p className="text-[9px] text-slate-400 font-mono truncate">Elevated Shizuku Bridge</p>
            </div>
          </button>
        </div>
      </div>

      {/* 6. Auxiliary Production Decks (Secondary Gear) */}
      <div className="p-3 rounded-2xl bg-black/40 border border-white/5 space-y-2">
        <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
          <span>AUXILIARY PRODUCTION GEAR (SECONDARY)</span>
          <button
            onClick={() => setActiveTab('devices')}
            className="text-fissal-gold/80 hover:text-fissal-gold text-[9px] flex items-center gap-0.5"
          >
            <span>Open Decks</span>
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              triggerHaptic(20);
              setActiveTab('devices');
            }}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-left flex items-center gap-2.5 transition-all"
          >
            <Sliders className="w-4 h-4 text-red-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-slate-300 truncate">Sound Rig (X32)</div>
              <div className="text-[9px] text-slate-500 font-mono truncate">Motorized DCA faders</div>
            </div>
          </button>

          <button
            onClick={() => {
              triggerHaptic(20);
              setActiveTab('devices');
            }}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-left flex items-center gap-2.5 transition-all"
          >
            <Flame className="w-4 h-4 text-amber-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-slate-300 truncate">Stage Lighting</div>
              <div className="text-[9px] text-slate-500 font-mono truncate">DMX Lekos & Master</div>
            </div>
          </button>
        </div>
      </div>

      {/* Focused Camera PTZ Controller Modal */}
      {selectedCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-lg rounded-3xl bg-slate-950 border border-fissal-gold/50 shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-amber-950/50 via-slate-900 to-black border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-sky-500/20 border border-sky-400 flex items-center justify-center text-sky-400">
                  <Video className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-white font-cinzel">{selectedCamera.name}</h3>
                  <p className="text-[10px] text-slate-400 font-mono">{selectedCamera.location || selectedCamera.ip}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {selectedCamera.light && (
                  <button
                    onClick={async () => {
                      triggerHaptic(20);
                      const next = selectedCamera.light_state === 'on' ? 'off' : 'on';
                      await postCameraLight(selectedCamera.id, next);
                      setSelectedCamera({ ...selectedCamera, light_state: next });
                    }}
                    className={"px-2.5 py-1 rounded-xl text-xs font-mono flex items-center gap-1 border transition-all " + (selectedCamera.light_state === 'on' ? 'bg-amber-500 text-slate-950 font-bold border-amber-400' : 'bg-white/5 text-slate-300 border-white/10 hover:text-white')}
                    title="Toggle Camera Floodlight"
                  >
                    <Lightbulb className="w-3.5 h-3.5" />
                    <span>{selectedCamera.light_state === 'on' ? 'Light ON' : 'Light OFF'}</span>
                  </button>
                )}
                <button
                  onClick={() => setSelectedCamera(null)}
                  className="p-1.5 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Live Camera View */}
            <div className="h-56 bg-black relative flex items-center justify-center">
              <img
                src={getFrigateSnapshotUrl(selectedCamera.id, cacheBuster)}
                alt={selectedCamera.name}
                className="w-full h-full object-contain"
              />
              {ptzFeedback && (
                <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-black/80 border border-fissal-gold/40 text-fissal-gold text-[10px] font-mono animate-pulse">
                  {ptzFeedback}
                </div>
              )}
            </div>

            {/* PTZ D-Pad Controls */}
            {selectedCamera.ptz && (
              <div className="p-4 border-t border-white/10 bg-slate-900/60 space-y-3">
                <div className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider text-center">
                  ONVIF PTZ DIRECTIONAL MOTOR
                </div>
                <div className="flex items-center justify-center gap-3">
                  <div className="grid grid-cols-3 gap-1.5 w-36">
                    <div />
                    <button
                      disabled={ptzPending}
                      onClick={() => handlePtzAction('up')}
                      className="p-3 rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-amber-500/30 text-white font-bold text-xs flex items-center justify-center"
                    >
                      ▲
                    </button>
                    <div />
                    <button
                      disabled={ptzPending}
                      onClick={() => handlePtzAction('left')}
                      className="p-3 rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-amber-500/30 text-white font-bold text-xs flex items-center justify-center"
                    >
                      ◀
                    </button>
                    <div className="p-3 rounded-xl bg-black/40 flex items-center justify-center text-[10px] font-mono text-fissal-gold">
                      PTZ
                    </div>
                    <button
                      disabled={ptzPending}
                      onClick={() => handlePtzAction('right')}
                      className="p-3 rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-amber-500/30 text-white font-bold text-xs flex items-center justify-center"
                    >
                      ▶
                    </button>
                    <div />
                    <button
                      disabled={ptzPending}
                      onClick={() => handlePtzAction('down')}
                      className="p-3 rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-amber-500/30 text-white font-bold text-xs flex items-center justify-center"
                    >
                      ▼
                    </button>
                    <div />
                  </div>

                  {/* Preset Buttons */}
                  {selectedCamera.presets && selectedCamera.presets.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      {selectedCamera.presets.map((preset) => (
                        <button
                          key={preset}
                          onClick={() => handlePresetAction(preset)}
                          className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-fissal-gold text-slate-300 text-[10px] font-mono capitalize transition-all"
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hands-Free Gemini 3.8 Live Voice Studio Modal */}
      <GeminiLiveVoiceModal
        isOpen={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
      />
    </div>
  );
};
